import { ClearOutlined, EditOutlined, DeleteOutlined, Save } from '@mui/icons-material';
import {
  GridActionsCellItem,
  DataGridProProps,
  GridRowModesModel,
  GridRowModes,
  GridRowId,
  GridApi,
} from '@mui/x-data-grid-pro';
import { useState, useMemo, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { SmartLinkRow } from './SmatLinkGridForm.types';

type UseSmartLinkDataGridActionsProps = Pick<DataGridProProps<SmartLinkRow>, 'columns'> & {
  apiRef: MutableRefObject<GridApi>;
  onAdd: () => SmartLinkRow;
  onDelete: (id: string) => void;
  onCancel: (id: string) => void;
};

type UseSmartLinkDataGridActionsResult = Pick<
  DataGridProProps<SmartLinkRow>,
  'columns' | 'rowModesModel' | 'onRowModesModelChange'
> & {
  onRowAdd: () => void;
};

export const useSmartLinkDataGridActions = ({
  apiRef,
  columns: columnsProp,
  onDelete,
  onAdd,
  onCancel,
}: UseSmartLinkDataGridActionsProps): UseSmartLinkDataGridActionsResult => {
  const { t } = useTranslation(undefined, { keyPrefix: 'DataGrid.column' });
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const columns: DataGridProProps<SmartLinkRow>['columns'] = useMemo(() => {
    const handleEditClick = (id: GridRowId) => () => {
      setRowModesModel((prevRowModesModel) => ({
        ...prevRowModesModel,
        [id]: { mode: GridRowModes.Edit },
      }));
    };
    const handleSaveClick = (id: GridRowId) => () => {
      setRowModesModel((prevRowModesModel) => ({
        ...prevRowModesModel,
        [id]: { mode: GridRowModes.View },
      }));
    };
    const handleDeleteClick = (id: GridRowId) => async () => {
      onDelete(`${id}`);
    };

    const handleCancelClick = (id: GridRowId) => () => {
      setRowModesModel((prevRowModesModel) => ({
        ...prevRowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));
      onCancel(`${id}`);
    };

    return [
      ...columnsProp,
      {
        field: 'actions',
        type: 'actions',
        headerName: t('actions'),
        width: 100,
        resizable: false,
        hideable: false,
        getActions: ({ id }: any) => {
          const isInEditMode = apiRef.current?.getRowMode(id) === GridRowModes.Edit;

          if (isInEditMode) {
            return [
              <GridActionsCellItem key="save" icon={<Save />} label="Save" onClick={handleSaveClick(id)} />,
              <GridActionsCellItem
                key="cancel"
                icon={<ClearOutlined />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ];
          }

          return [
            <GridActionsCellItem
              key="edit"
              icon={<EditOutlined />}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(id)}
              color="inherit"
            />,
            <GridActionsCellItem
              key="del"
              icon={<DeleteOutlined />}
              label="Delete"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />,
          ];
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsProp, t]);

  const addRow = () => {
    const newRow = onAdd();
    newRow.isNew = true;
    setRowModesModel((prevRowModesModel) => ({
      ...prevRowModesModel,
      [newRow.id]: { mode: GridRowModes.Edit },
    }));
  };

  return {
    columns,
    rowModesModel,
    onRowModesModelChange: setRowModesModel,
    onRowAdd: addRow,
  };
};
